import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { BookOpen, Headphones, Heart } from 'lucide-react';

export const WelcomeModal = ({ user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Ne pas afficher pour les admins ou sur les pages admin
    if (user?.is_admin || location.pathname.startsWith('/admin')) {
      return;
    }
    
    // Vérifier si c'est la première visite de l'utilisateur
    const welcomeShown = localStorage.getItem(`welcome_shown_${user?.id}`);
    if (!welcomeShown && user?.id) {
      setIsOpen(true);
    }
  }, [user?.id, user?.is_admin, location.pathname]);

  const handleClose = () => {
    // Marquer comme vu
    if (user?.id) {
      localStorage.setItem(`welcome_shown_${user.id}`, 'true');
    }
    setIsOpen(false);
  };

  const handleGoToGuide = () => {
    handleClose();
    navigate('/aide');
  };

  const handleGoToProgram = () => {
    handleClose();
    navigate('/programme');
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent className="max-w-md mx-4 bg-white rounded-2xl" data-testid="welcome-modal">
        <DialogHeader className="text-center pb-2">
          <div className="mx-auto w-16 h-16 bg-sage-100 rounded-full flex items-center justify-center mb-4">
            <Heart className="w-8 h-8 text-sage-600" />
          </div>
          <DialogTitle className="text-2xl font-serif font-light text-sage-700">
            Bienvenue dans Déclic RePas
          </DialogTitle>
          <DialogDescription className="text-sage-600 mt-2">
            Vous êtes sur le point de commencer un voyage vers une relation plus sereine avec l'alimentation.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="bg-sage-50 rounded-xl p-4">
            <h3 className="font-sans font-medium text-sage-700 mb-2 flex items-center">
              <BookOpen className="w-5 h-5 mr-2 text-sage-500" />
              Avant de commencer
            </h3>
            <p className="text-sm text-sage-600">
              Nous vous recommandons de lire le <strong>Guide Pratique</strong> dans la section Aide. 
              Il vous explique comment fonctionne l'inconscient et comment écouter vos séances pour un maximum d'efficacité.
            </p>
          </div>

          <div className="bg-sand-50 rounded-xl p-4">
            <h3 className="font-sans font-medium text-sage-700 mb-2 flex items-center">
              <Headphones className="w-5 h-5 mr-2 text-sage-500" />
              Votre parcours
            </h3>
            <p className="text-sm text-sage-600">
              Le programme comprend 11 thèmes audio. Alternez entre les versions <strong>Zen</strong> (assis) 
              et <strong>Mouvement</strong> (en marchant), avec une pause d'un jour entre chaque thème.
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-3 pt-2">
          <Button
            onClick={handleGoToGuide}
            className="w-full bg-sage-600 hover:bg-sage-600 text-white h-12 rounded-xl"
            data-testid="welcome-go-to-guide"
          >
            <BookOpen className="w-4 h-4 mr-2" />
            Lire le Guide Pratique
          </Button>
          <Button
            onClick={handleGoToProgram}
            variant="outline"
            className="w-full border-sage-300 text-sage-700 hover:bg-sage-50 h-12 rounded-xl"
            data-testid="welcome-go-to-program"
          >
            <Headphones className="w-4 h-4 mr-2" />
            Commencer le Programme
          </Button>
          <button
            onClick={handleClose}
            className="text-sm text-sage-500 hover:text-sage-700 py-2"
            data-testid="welcome-close"
          >
            Fermer et explorer
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WelcomeModal;
